import { fetchBaseQuery } from "@reduxjs/toolkit/dist/query";
import { configs } from "./config";
import {
  GetTokenSilentlyOptions,
  LogoutOptions,
  RedirectLoginOptions,
} from "@auth0/auth0-react";
import { anonymizeFullStorySession } from "src/shared/utilities/fullStorySession";
import {
  stashAuthReturnTo,
  shouldSkipAuthReturnTo,
} from "src/shared/utilities/authRedirect";

let getAccessTokenSilently: (
  options?: GetTokenSilentlyOptions | undefined
) => Promise<string>;

let logout: (options?: LogoutOptions | undefined) => Promise<void>;

let loginWithRedirect: (
  options?: RedirectLoginOptions | undefined
) => Promise<void>;

// Guard so a burst of privateApi calls with a dead token only triggers one
// Auth0 redirect (prepareHeaders runs per request).
let softRecoverRedirecting = false;

// Pre-sign correction ("scoped ops impersonation"): when the resident editor is
// opened from the admin correction deep link (#correction=<token>), it rides this
// short-lived, application-scoped token as its API bearer INSTEAD of an Auth0
// session. Held in memory only (never persisted) and set once, synchronously, at
// app boot before any query fires. See setCorrectionTokenFromLocation.
let correctionToken: string | null = null;

// Ops "Login as user" (read-only Home impersonation): same fragment pattern as
// correction (#loginAs=<token>). See setLoginAsTokenFromLocation.
let loginAsToken: string | null = null;

export const securityOptions = {
  getAccessTokenSilently: () => getAccessTokenSilently,
  setAccessTokenSilently: (
    func: (options?: GetTokenSilentlyOptions | undefined) => Promise<string>
  ) => (getAccessTokenSilently = func),
  logout: () => logout,
  setLogout: (func: (options?: LogoutOptions | undefined) => Promise<void>) =>
    (logout = func),
  loginWithRedirect: () => loginWithRedirect,
  setLoginWithRedirect: (
    func: (options?: RedirectLoginOptions | undefined) => Promise<void>
  ) => (loginWithRedirect = func),
  getCorrectionToken: () => correctionToken,
  setCorrectionToken: (token: string | null) => (correctionToken = token),
  getLoginAsToken: () => loginAsToken,
  setLoginAsToken: (token: string | null) => (loginAsToken = token),
  /** True when the session is an ops grant (correction or login-as), not Auth0. */
  hasOpsGrant: () => !!(correctionToken || loginAsToken),
  /** Test helper — reset soft-recover redirect latch. */
  _resetSoftRecoverForTests: () => {
    softRecoverRedirecting = false;
  },
};

/**
 * Soft-recover when getAccessTokenSilently fails: stash returnTo and
 * loginWithRedirect. Do NOT Auth0 logout() — that kills SSO/refresh and forces
 * a hard password/invite path (applicant "kicked out / can't get back in").
 */
export async function softRecoverAuthSession(): Promise<void> {
  if (typeof window === "undefined") return;
  if (softRecoverRedirecting) return;

  const returnTo = `${window.location.pathname}${window.location.search}`;
  if (shouldSkipAuthReturnTo(returnTo)) return;

  softRecoverRedirecting = true;
  anonymizeFullStorySession();
  stashAuthReturnTo(returnTo);

  const redirect = securityOptions.loginWithRedirect();
  if (!redirect) {
    softRecoverRedirecting = false;
    return;
  }

  try {
    await redirect({
      authorizationParams: {
        responseType: "code",
        redirectUri: `${window.location.origin}`,
        mode: "login",
      },
      appState: { returnTo },
    });
  } catch {
    softRecoverRedirecting = false;
  }
}

/**
 * Read a correction token from the URL fragment (#correction=<token>) once at
 * boot, store it in memory, and strip it from the address bar so it does not
 * linger in history / screenshots. Fragments are never sent to the server, so
 * this keeps the token out of access logs. No-op when the fragment is absent.
 */
export const setCorrectionTokenFromLocation = () => {
  try {
    const hash = window.location.hash || "";
    const match = hash.match(/[#&]correction=([^&]+)/);
    if (match && match[1]) {
      correctionToken = decodeURIComponent(match[1]);
      const cleaned = hash
        .replace(/([#&])correction=[^&]+/, "$1")
        .replace(/[#&]$/, "");
      window.history.replaceState(
        null,
        "",
        window.location.pathname +
          window.location.search +
          (cleaned && cleaned !== "#" ? cleaned : "")
      );
    }
  } catch {
    // Non-browser / restricted context — correction mode simply stays off.
  }
};

/**
 * Read an ops login-as token from `#loginAs=<token>` (same fragment hygiene as
 * correction). Opens the resident Home as that user without Auth0.
 */
export const setLoginAsTokenFromLocation = () => {
  try {
    const hash = window.location.hash || "";
    const match = hash.match(/[#&]loginAs=([^&]+)/);
    if (match && match[1]) {
      loginAsToken = decodeURIComponent(match[1]);
      const cleaned = hash
        .replace(/([#&])loginAs=[^&]+/, "$1")
        .replace(/[#&]$/, "");
      window.history.replaceState(
        null,
        "",
        window.location.pathname +
          window.location.search +
          (cleaned && cleaned !== "#" ? cleaned : "")
      );
    }
  } catch {
    // Non-browser — login-as mode stays off.
  }
};

export const prepareHeaders = async (headers: any) => {
  // Ops grants: use the impersonation token and DO NOT touch Auth0 (there is
  // no ops Auth0 session; calling getAccessTokenSilently would throw → soft recover).
  const correction = securityOptions.getCorrectionToken();
  if (correction) {
    headers.set("authorization", `Bearer ${correction}`);
    headers.set("Content-Type", "application/json");
    return headers;
  }
  const loginAs = securityOptions.getLoginAsToken();
  if (loginAs) {
    headers.set("authorization", `Bearer ${loginAs}`);
    headers.set("Content-Type", "application/json");
    return headers;
  }

  try {
    const access_token = await securityOptions.getAccessTokenSilently()();
    if (access_token) {
      headers.set("authorization", `Bearer ${access_token}`);
    }
  } catch (e) {
    await softRecoverAuthSession();
    return headers;
  }

  headers.set("Content-Type", "application/json");
  return headers;
};

export const prepareHeadersAuthLess = (headers: any) => {
  headers.set("Content-Type", "application/json");
  return headers;
};

export const prepareBaseQuery = (isAuthLess: boolean) => {
  return fetchBaseQuery({
    baseUrl: configs.base_url,
    prepareHeaders: isAuthLess ? prepareHeadersAuthLess : prepareHeaders,
  });
};

export const prepareProofupQuery = () => {
  return fetchBaseQuery({
    baseUrl: configs.proofup_url,
  });
};
